import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { getErrorData, getErrorMessages, isErrorResponse } from "@/helpers/reduxHelpers";
import { RawErrorResponse } from "@/redux/types";

export const getVerificationErrorType = (error: FetchBaseQueryError | SerializedError | undefined) => {
  if (!error || !isErrorResponse(error)) return undefined;

  const messages = getErrorMessages(error || {}).join(", ");
  if (messages.includes("Vehicle verification failed")) return "verification-failed";

  const errorData = getErrorData(error as RawErrorResponse);
  if (!errorData) return "verification-failed";

  const { error: msg, data } = errorData;

  switch (msg) {
    case "VEHICLE_EXISTS":
      return data?.is_archived && data?.is_own_vehicle
        ? `vehicle-archived-${data.vehicle_uuid}`
        : data?.is_own_vehicle
        ? "vehicle-exists-duplicate"
        : "vehicle-exists";
    case "NO_MATCH":
      return "no-match";
    default:
      return "verification-failed";
  }
};

/**
 * Which wizard steps a vehicle actually has.
 *
 * The numbers are the BACKEND's `setup_step`, not display positions — that
 * field is persisted per vehicle and drives "Finish later" resumption, so it
 * stays 1-4 even when step 3 does not render. Renumbering would strand every
 * vehicle already saved mid-setup and would mean changing the PHP contract for
 * a cosmetic gain.
 *
 * Step 3 drops out in exactly one case: KYV is on AND the vehicle is already
 * verified. There, step 3 exists only to RUN verification, a VIN-first vehicle
 * arrives already verified, and Veivo has collected the insurance and
 * accreditation documents the wizard used to ask for — nothing is left for it
 * to do.
 *
 * On the plate flow step 3 always stays, whatever `alreadyVerified` says. It is
 * the documents page there, and nothing else collects them.
 */
export const stepSequenceFor = (kyvEnabled: boolean, alreadyVerified: boolean): number[] =>
  kyvEnabled && alreadyVerified ? [1, 2, 4] : [1, 2, 3, 4];

/**
 * The step to actually render for a saved `setup_step`.
 *
 * A vehicle saved at `setup_step: 3` on a flow where step 3 does not apply
 * resumes onto a step that is not in its sequence. Move FORWARD to the next
 * step that is, rather than rendering nothing — and never backwards, which
 * would ask the operator to redo work they have already finished.
 */
export const resolveActiveStep = (sequence: number[], savedStep: number): number =>
  sequence.find((step) => step >= savedStep) ?? sequence[sequence.length - 1];
